/* ============================================================
   Dathent — upcomingSchedule.js
   GH-975: the Dashboard "Scheduled · 7d" card read 0 for everyone. It
   counted api.scheduler.list({ limit: 20 }) — the 20 OLDEST scheduled
   posts — and read flat item.scheduledAt fields, while scheduler rows are
   { post: { scheduledAt, status }, content, targets: [{ status,
   scheduledAt }], pendingIntent? }.

   This counts scheduler/calendar rows the way the scheduler-scanner will
   actually dispatch them, one per post (content item — a clone-republish
   can split one post across two scheduled_posts, the same dedupe Calendar
   expandToSlots does):
     - post.status is approved / queued / scheduled, AND
     - a pending/queued target is due in [now, now + 7d] (its own
       scheduledAt, else the post's), or the post has no targets and its
       scheduledAt is in the window;
     - a waiting schedule intent (pendingIntent.status 'pending', no
       targets yet) counts when its time is in the window — it publishes
       once its content is ready.
   Flat legacy rows (no `post` wrapper) are read as their own post.
   ============================================================ */

const DISPATCHABLE_POST_STATUSES = new Set(['approved', 'queued', 'scheduled']);
const WAITING_TARGET_STATUSES = new Set(['pending', 'queued']);
const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function scheduledTime(row) {
  const when = row?.scheduledAt || row?.scheduled_at || row?.publishAt || row?.publish_at;
  if (!when) return null;
  const time = new Date(when).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * @param {Array<object>} items  scheduler/calendar rows
 * @param {Date} [now]
 * @returns {number} scheduled posts that will publish within the next 7 days
 */
export function scheduledNext7Days(items = [], now = new Date()) {
  const from = now.getTime();
  const to = from + WINDOW_MS;
  const inWindow = (time) => time !== null && time >= from && time <= to;
  const posts = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    const post = item?.post || item;
    if (!post) continue;
    const postTime = scheduledTime(post);
    const key = post.contentItemId || post.content_item_id || item.content?.contentItem?.id || post.id || item;
    if (item.pendingIntent) {
      if (item.pendingIntent.status === 'pending' && inWindow(postTime)) posts.add(key);
      continue;
    }
    if (!DISPATCHABLE_POST_STATUSES.has(post.status)) continue;
    const targets = Array.isArray(item.targets) ? item.targets : [];
    const upcoming = targets.length
      ? targets.some((target) => WAITING_TARGET_STATUSES.has(target?.status)
        && inWindow(scheduledTime(target) ?? postTime))
      : inWindow(postTime);
    if (upcoming) posts.add(key);
  }
  return posts.size;
}
